import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CompanyApi } from '@api/companies.api';
import { SegmentApi } from '@api/segments.api';
import { MaintenanceTypeService } from '@core/components/inputs/maintenance-type/maintenance-type.service';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { MaintenanceType, Recurrence, RecurrenceDialogData } from '@interfaces';
import { DataRangeValidation } from '@validators/date-range-validation';
import { DateValidation } from '@validators/date.validation';
import { sortBy } from 'lodash';
import { merge, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CustomMessagesService } from '../../../core/errors/custom-messages.service';
import { mapLinksInput } from '../../../core/utils/map-links-input/map-links-Input';
import { IntegerValueValidation } from '../../../core/validators/integer-value.validation';
import { RecurrenceService } from '../services/recurrence.service';
import english from './../i18n/en.json';
import spanish from './../i18n/es.json';
import portuguese from './../i18n/pt.json';
import { DialogAlertComponent } from './dialog-alert/dialog-alert.component';

@Component({
  selector: 'dialog-recurrences',
  templateUrl: './dialog-recurrences.component.html',
  styleUrls: ['./dialog-recurrences.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogRecurrencesComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subject<void>();
  private readonly recurrencesReload: Subject<void>;

  public recurrenceForm: FormGroup;
  public recurrence: Recurrence;
  public isViewMode: boolean;

  public isLoading = false;
  public isUpdate = false;
  public refreshDatatable = false;
  public disableLinks = false;

  public maintenanceTypes: MaintenanceType[] = [];
  public isFormValid: boolean;
  private isCopyMode: boolean;


  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: RecurrenceDialogData,
    private readonly matDialogRef: MatDialogRef<DialogRecurrencesComponent>,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly matDialog: MatDialog,
    private readonly messageService: CustomMessagesService,
    private readonly recurrenceService: RecurrenceService,
    private readonly formBuilder: FormBuilder,
    private readonly companyApi: CompanyApi,
    private readonly maintenanceTypeService: MaintenanceTypeService,
    private readonly segmentApi: SegmentApi,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });

    this.recurrence = this.dialogData.recurrence;
    this.isViewMode = this.dialogData.isViewMode || this.recurrence.locked;
    this.isUpdate = !!this.recurrence && !!this.recurrence.id;
    this.isCopyMode = this.dialogData.isCopyMode;
    this.recurrencesReload = this.dialogData.recurrencesReload;

    const disabled = this.isUpdate;
    const repeat = this.recurrence.repeat || {};

    this.recurrenceForm = this.formBuilder.group({
      location: [{ disabled, value: this.recurrence.location }, Validators.required],
      segment: [{ value: this.recurrence.segment, disabled: this.isUpdate }, Validators.required],
      maintenanceType: [{ disabled, value: this.recurrence.maintenanceType }, Validators.required],
      message: [this.recurrence.message, [Validators.required, Validators.maxLength(1500)]],
      company: [{ disabled: true, value: this.recurrence.company }],
      from: new FormControl({ disabled, value: repeat.from || new Date() }, [Validators.required]),
      every: [{ disabled, value: repeat.every || 1 }, [Validators.required, Validators.min(1), Validators.max(360), IntegerValueValidation.validate]],
      interval: [{ disabled, value: repeat.interval || 'daily' }, Validators.required],
      weekday: [{ disabled, value: repeat.weekday || 'monday' }],
      dayOfMonth: [{ disabled, value: repeat.dayOfMonth || 1 }, [Validators.min(1), Validators.max(28), IntegerValueValidation.validate]],
      skipWeekend: [{ disabled, value: repeat.skipWeekend || false }, Validators.required],
      until: [{ disabled, value: repeat.until }, [DateValidation.validate]],
      newEquipments: new FormControl(this.recurrence.equipments),
      locationSectors: new FormControl(this.recurrence.locationSectors),
      locationEnvironments: new FormControl(this.recurrence.locationEnvironments)
    }, {
      validators: [DataRangeValidation.validate]
    });

    if (this.isViewMode) {
      this.recurrenceForm.disable();
    }
    if (this.isCopyMode) {
      this.recurrenceForm.updateValueAndValidity();
      this.recurrenceForm.markAllAsTouched();
    }
  }

  ngOnInit(): void {
    const maintenanceTypeControl = this.recurrenceForm.get('maintenanceType');
    const segmentControl = this.recurrenceForm.get('segment');
    const locationControl = this.recurrenceForm.get('location');
    const companyControl = this.recurrenceForm.get('company');

    const locationChanges = locationControl.valueChanges;
    const segmentChanges = segmentControl.valueChanges;
    const maintenanceTypeChanges = maintenanceTypeControl.valueChanges;
    const segmentOrLocationChanges = merge(segmentChanges, locationChanges);
    const segmentOrLocationOrMaintenanceTypeChanges = merge(segmentChanges, locationChanges, maintenanceTypeChanges);

    this.recurrenceForm.valueChanges.pipe(takeUntil(this.subscriptions))
      .subscribe(() => {
        this.disableLinks = !locationControl.value || !segmentControl.value;
      });

    locationChanges.pipe(takeUntil(this.subscriptions))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });

    segmentOrLocationChanges.pipe(takeUntil(this.subscriptions))
      .subscribe(async () => {
        const selectedSegmentId = segmentControl.value && segmentControl.value.id;
        const selectedLocationId = locationControl.value && locationControl.value.id;

        const recurrenceSegmentId = this.recurrence && this.recurrence.segment && this.recurrence.segment.id;
        const recurrenceLocationId = this.recurrence && this.recurrence.location && this.recurrence.location.id;
        const isSameSegment = selectedSegmentId === recurrenceSegmentId;
        const isSameLocation = selectedLocationId === recurrenceLocationId;
        if (!isSameSegment || !isSameLocation) {
          this.recurrenceForm.get('newEquipments').patchValue([]);
          this.recurrenceForm.get('locationSectors').patchValue([]);
          this.recurrenceForm.get('locationEnvironments').patchValue([]);
        }
      });

    segmentOrLocationOrMaintenanceTypeChanges.pipe(
      takeUntil(this.subscriptions),
      map(() => {
        return {
          segmentId: segmentControl.value && segmentControl.value.id,
          locationId: locationControl.value && locationControl.value.id,
          maintenanceTypeId: maintenanceTypeControl.value && maintenanceTypeControl.value.id
        };
      }),
      tap(() => {
        companyControl.setValue(null);
        this.fuseProgressBarService.show();
      }),
      filter(({ segmentId, locationId, maintenanceTypeId }) => {
        return segmentId && locationId && maintenanceTypeId;
      }),
      switchMap(({ segmentId, locationId, maintenanceTypeId }) => {
        return this.companyApi.getCompanyByConfig({ locationId, segmentId, maintenanceTypeId });
      }),
      tap(() => {
        this.fuseProgressBarService.hide();
      }),
      filter((company) => !!company),
      tap((company) => {
        companyControl.setValue(company);
      })
    ).subscribe();

    segmentChanges.subscribe(async (segment) => {
      const segmentMaintenanceTypes = await this.getSelectedSegmentMaintenanceTypes(segment);

      const selectedMaintenanceType = this.recurrence.maintenanceType;

      if (segmentMaintenanceTypes.length) {
        this.maintenanceTypes = segmentMaintenanceTypes;
      } else {
        this.maintenanceTypes = await this.getAllMaintenanceTypes();
      }


      if (selectedMaintenanceType) {
        const selectedMaintenanceTypeIsOnList = this.maintenanceTypes.some((maintenanceType) => {
          return maintenanceType.id === selectedMaintenanceType.id;
        });

        if (!selectedMaintenanceTypeIsOnList) {
          this.maintenanceTypes.push(selectedMaintenanceType);
          this.maintenanceTypes = sortBy(this.maintenanceTypes, ['name']);
        }
      }
    });

    segmentChanges.pipe(takeUntil(this.subscriptions))
      .subscribe(async (segment) => {
        if (segment && segment.id && !this.isUpdate) {
          maintenanceTypeControl.enable();
        } else {
          maintenanceTypeControl.disable();
        }
      });

    if (this.isUpdate) {
      const currentMaintenanceTypeIsOnList = this.maintenanceTypes.some((maintenanceType) => {
        return maintenanceType.id === this.recurrence.maintenanceType.id;
      });

      if (!currentMaintenanceTypeIsOnList) {
        this.maintenanceTypes.push(this.recurrence.maintenanceType);
        this.maintenanceTypes = sortBy(this.maintenanceTypes, ['name']);
      }
    }

    if (this.recurrence && this.recurrence.id) {
      this.recurrenceForm.markAllAsTouched();
    }

    this.recurrenceForm.statusChanges.subscribe(() => {
      setTimeout(() => {
        this.isFormValid = this.recurrenceForm.valid;
      });
    });
  }

  async getAllMaintenanceTypes(): Promise<any> {
    const allMaintenanceTypes = await this.maintenanceTypeService.listMaintenanceTypes({
      where: {
        archived: false,
        type_in: ['preventiveMaintenance', 'customMaintenance']
      }
    });
    return allMaintenanceTypes;
  }

  async getSelectedSegmentMaintenanceTypes(segment: any): Promise<any> {
    if (!segment) { return []; }
    const { maintenanceTypes } = await this.segmentApi.searchOne(segment.id);
    return maintenanceTypes;
  }

  public async copyRecurrence(): Promise<void> {
    if (this.isViewMode) { return; }

    this.matDialogRef.close(false);

    this.fuseProgressBarService.show();
    this.isLoading = true;
    const recurrence = await this.recurrenceService.getById(this.recurrence.id);

    delete recurrence.id;

    const dialogRecurrences = this.matDialog.open(DialogRecurrencesComponent, {
      panelClass: 'dialog-recurrences',
      data: {
        recurrence,
        isViewMode: this.isViewMode,
        recurrencesReload: this.recurrencesReload,
        isCopyMode: true
      }
    });

    this.isLoading = false;
    this.fuseProgressBarService.hide();

    this.messageService.success('RECURRENCES.DIALOG.COPIED');

    const reload = await dialogRecurrences.afterClosed().toPromise();
    if (reload) {
      this.recurrencesReload.next();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }


  async openArchiveOrUnarchiveDialog(): Promise<void> {
    const archiveOrUnarchiveDialog = this.matDialog.open(DialogAlertComponent, {
      panelClass: 'dialog-alert',
      data: this.recurrence && this.recurrence.archived
    });

    const archived = await archiveOrUnarchiveDialog.afterClosed().toPromise();
    if (typeof archived !== 'boolean') {
      return;
    }

    this.recurrence.archived = archived;
    await this.saveRecurrence();
  }


  async saveRecurrence(): Promise<void> {
    this.fuseProgressBarService.show();
    this.isLoading = true;
    const formValue = this.recurrenceForm.getRawValue();
    const recurrence = {
      locationId: formValue.location.id,
      typeId: formValue.maintenanceType.id,
      segmentId: formValue.segment.id,
      message: formValue.message,
      archived: this.recurrence && this.recurrence.archived || false,
      repeat: {
        from: formValue.from,
        every: formValue.every ? parseInt(formValue.every, 10) : null,
        interval: formValue.interval,
        weekDay: formValue.weekday,
        dayOfMonth: parseInt(formValue.dayOfMonth, 10),
        skipWeekend: formValue.skipWeekend,
        until: formValue.until
      }
    };

    Object.assign(recurrence, mapLinksInput({
      locationSectors: formValue.locationSectors,
      locationEnvironments: formValue.locationEnvironments,
      newEquipments: formValue.newEquipments
    }));

    if (this.recurrence.id) {
      delete recurrence.repeat;
      delete recurrence.typeId;
    }

    await this.recurrenceService.save(this.recurrence.id, recurrence)
      .then(async (result) => {
        this.refreshDatatable = true;
        this.matDialogRef.close(true);

        const saved = await this.recurrenceService.getById(result.id);
      }).finally(() => {
        this.fuseProgressBarService.hide();
        this.isLoading = false;
      });

  }
}
